import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrganisationMembership } from '@ghostscreen/contracts';
import { brandAssets } from '../../lib/brandAssets';
import { describeOrganisationPlayback } from '../../lib/organisation/organisationHealth';
import { SHOWCASE_BOARDS, showcaseBoardIndex } from './showcaseBoards';

type OrganisationShowcaseProps = {
  /** Business being previewed, or null before any exist. */
  organisation: OrganisationMembership | null;
  organisationIndex: number;
};

/** Decorative left panel: a TV previewing signage for the highlighted business. */
export function OrganisationShowcase({ organisation, organisationIndex }: OrganisationShowcaseProps) {
  const { t } = useTranslation('auth');
  const activeBoard = showcaseBoardIndex(organisationIndex);
  const board = SHOWCASE_BOARDS[activeBoard]!;
  const playback = organisation
    ? describeOrganisationPlayback(organisation)
    : { isLive: false, caption: t('chooseOrganisation.showcase.noOrganisation') };

  return (
    <section
      aria-hidden="true"
      className="org-showcase"
      style={{ '--tint-light': board.tint.light, '--tint-dark': board.tint.dark } as CSSProperties}
    >
      <div className="org-showcase__glow" style={{ backgroundImage: `url(${board.src})` }} />

      <div className="org-showcase__brand">
        <span className="org-showcase__mark">
          <img className="org-showcase__mark-light" src={brandAssets.monitorIconOnLight} alt="" />
          <img className="org-showcase__mark-dark" src={brandAssets.monitorIconOnDark} alt="" />
        </span>
        GhostScreens
      </div>

      <div className="org-showcase__stage">
        <div className="org-tv">
          <div className="org-tv__bezel">
            <div className="org-tv__screen">
              {SHOWCASE_BOARDS.map((item, index) => (
                <div
                  key={item.src}
                  className="org-tv__board"
                  data-active={index === activeBoard}
                  style={{ backgroundImage: `url(${item.src})` }}
                />
              ))}
              <div className="org-tv__glare" />
            </div>
          </div>
          <div className="org-tv__neck" />
          <div className="org-tv__foot" />
        </div>

        <div className="org-live-pill" data-live={playback.isLive}>
          <span className="org-live-pill__dot" />
          <span className="org-live-pill__label">
            {playback.isLive
              ? t('chooseOrganisation.showcase.live')
              : t('chooseOrganisation.showcase.idle')}
          </span>
          <span>{playback.caption}</span>
        </div>
      </div>
    </section>
  );
}
